/**
 * triageDefaultsByCategory.js
 * ---------------------------
 * Pre-populated call-triage rules shown to a free user the FIRST time
 * they reach Step 5 (Triage Rules) of the GoLiveWizard. The list is
 * vertical-specific: a Plumber sees "Emergency / Urgent" rows where a
 * Restaurant sees "Reservation request" instead.
 *
 * Source: REXPT.pdf §2 Phase 4 — Screen 13 Step 3 ("Tell Sophia When to
 * Transfer Calls"). The spec gives 4 generic example rows and says
 * "vertical-specific smart defaults" — this file is that mapping.
 *
 * Behaviour notes:
 *   - All rows are pre-filled with the BUSINESS PHONE (per user spec).
 *     The user changes per-row to actual department numbers (Owner mobile,
 *     On-call number, etc.) before saving.
 *   - User can delete any row and add custom ones via the "+ Add Rule"
 *     button.
 *   - Rules only seed ONCE — first time the user lands on the triage step
 *     with zero existing rules. After that, never auto-add (so deleting
 *     defaults and revisiting doesn't re-spawn them).
 *   - PostPurchaseSetup does NOT pass these — only GoLiveWizard does.
 *
 * Adding a new category: just add a key matching the `type` string from
 * `lib/businessCategories.js` and provide an array of 3-4 rule shapes.
 * Categories not listed here fall back to `_default`.
 */

// Re-usable templates for groups of similar verticals — keeps the export
// small and lets owner/Kulbir tweak by template (one edit propagates to
// all categories using it) instead of editing 54 individual lists.

// NOTE: each row's `condition` is written as `Label — Description` on a
// single line. The em-dash separator is intentional: the existing
// `buildTransferPrompt` uses `condition` verbatim in the LLM transfer
// rules ("If they say <condition>, transfer to ..."), so embedding the
// description directly in `condition` gives the LLM the situational
// trigger it needs to decide WHEN to transfer — without adding a
// separate `notes` column to AgentTransferNumber. The custom-name input
// in the UI shows the full string, which the user can shorten or expand.

const T_TRADES = [
  { condition: "Emergency / Urgent — Any issue involving safety, property damage, or total utility failure" },
  { condition: "Sales / Quote request — customer wants a price or quote" },
  { condition: "Existing job follow-up — job updates, scheduling changes" },
];

const T_FOOD = [
  { condition: "Reservation request — table booking, seating preferences" },
  { condition: "Catering / Group order — large group orders, events" },
  { condition: "Existing customer — order status, complaints, feedback" },
];

const T_BEAUTY = [
  { condition: "Appointment booking — new booking or reschedule" },
  { condition: "Pricing inquiry — service pricing, packages, offers" },
  { condition: "Existing client — past client follow-up, queries" },
];

const T_AUTO = [
  { condition: "Service appointment — booking a repair / service slot" },
  { condition: "Pricing / Quote — customer wants a price or quote" },
  { condition: "Existing repair follow-up — job status, parts updates" },
];

const T_TRANSPORT = [
  { condition: "New booking — customer wants to book a ride / trip" },
  { condition: "Pricing inquiry — fare estimate, package pricing" },
  { condition: "Existing booking change — reschedule, cancellation, status" },
];

const T_REAL_ESTATE = [
  { condition: "Listing / Showing inquiry — viewing request, property questions" },
  { condition: "Pricing / Offer — price discussion, offers" },
  { condition: "Existing client follow-up — active deal updates, paperwork" },
];

const T_PROFESSIONAL = [
  { condition: "New consultation — first-time inquiry, scheduling" },
  { condition: "Pricing / Quote — service fees, retainer questions" },
  { condition: "Existing case / project — active matter updates, follow-up" },
];

const T_EDUCATION = [
  { condition: "Admission inquiry — new student / course interest" },
  { condition: "Fees / Pricing — fee structure, scholarship questions" },
  { condition: "Existing student — current student follow-up, parents" },
];

const T_HEALTH = [
  { condition: "New appointment — first-time patient / member booking" },
  { condition: "Pricing / Membership — service costs, membership plans" },
  { condition: "Existing patient — follow-up, results, scheduling" },
];

const T_RETAIL_GENERAL = [
  { condition: "Sales / Quote request — customer wants a price or quote" },
  { condition: "Emergency / Urgent — time-sensitive customer issue" },
  { condition: "Existing customer follow-up — order status, support, feedback" },
];

// Spec's literal example — used as the safety-net fallback when a category
// isn't in the map below.
const T_DEFAULT = [
  { condition: "Sales / Quote request — customer wants a price or quote" },
  { condition: "Emergency / Urgent — Any issue involving safety, property damage, or total utility failure" },
  { condition: "Existing customer follow-up — job updates, existing bookings" },
];

/*
 * Category → defaults map. Keys MUST exactly match the `type` strings in
 * `lib/businessCategories.js` (case-sensitive) — that's what gets stored
 * in business.businessType / business.category at agent creation.
 *
 * Categories grouped by template for easy maintenance. Add new keys here
 * to override the _default fallback.
 */
const TRIAGE_DEFAULTS_BY_CATEGORY = {
  // ── Skilled trades (urgency-driven verticals) ──
  "Plumber":                       T_TRADES,
  "Roofers":                       T_TRADES,
  "HVAC Services":                 T_TRADES,
  "Electrician":                   T_TRADES,
  "Home Services":                 T_TRADES,
  "Flooring":                      T_TRADES,
  "Construction Services":         T_TRADES,
  "General Contractor":            T_TRADES,
  "Engineering":                   T_TRADES,
  "Compliance & Inspection Services": T_TRADES,
  "Cleaning and Janitorial Services": T_TRADES,
  "Landscaping Company":           T_TRADES,

  // ── Food / Hospitality ──
  "Restaurant":                    T_FOOD,
  "Bakery":                        T_FOOD,
  "Deli Shop":                     T_FOOD,

  // ── Beauty / Wellness ──
  "Salon":                         T_BEAUTY,
  "Beauty Parlour":                T_BEAUTY,
  "Nail Salon":                    T_BEAUTY,
  "Hair Stylist":                  T_BEAUTY,
  "Barber Studio/Shop":            T_BEAUTY,
  "Spa & Wellness Center":         T_BEAUTY,

  // ── Auto / Mechanical Services ──
  "Car Repair & Garage":           T_AUTO,
  "Boat Repair":                   T_AUTO,
  "Car & Bus Services":            T_AUTO,

  // ── Transport / Booking ──
  "Taxi, Cab & Limo Booking":      T_TRANSPORT,
  "Taxi Service":                  T_TRANSPORT,
  "Limo Booking":                  T_TRANSPORT,
  "Movers and Packers":            T_TRANSPORT,
  "Trucking Company":              T_TRANSPORT,
  "Travel Agency":                 T_TRANSPORT,
  "Tour Guides":                   T_TRANSPORT,
  "Ticket Booking":                T_TRANSPORT,

  // ── Real Estate ──
  "Real Estate Broker":            T_REAL_ESTATE,
  "Property Rental & Leasing Service": T_REAL_ESTATE,

  // ── Professional Services ──
  "Law Firm":                      T_PROFESSIONAL,
  "Accounting Services":           T_PROFESSIONAL,
  "Financial Planners":            T_PROFESSIONAL,
  "Insurance Agency":              T_PROFESSIONAL,
  "Architect":                     T_PROFESSIONAL,
  "Interior Designer":             T_PROFESSIONAL,
  "Marketing Agency":              T_PROFESSIONAL,
  "Digital Marketing Agency":      T_PROFESSIONAL,
  "Web Design Agency":             T_PROFESSIONAL,
  "B2B Equipment & Solutions":     T_PROFESSIONAL,

  // ── Education ──
  "School":                        T_EDUCATION,
  "Colleges & Universities":       T_EDUCATION,
  "Training Center":               T_EDUCATION,
  "Educational Institute":         T_EDUCATION,

  // ── Health / Fitness ──
  "Dentist":                       T_HEALTH,
  "Gym & Fitness Center":          T_HEALTH,
  "Personal Trainer":              T_HEALTH,

  // ── Retail / Misc ──
  "Print Shop":                    T_RETAIL_GENERAL,
  "Dry Cleaners":                  T_RETAIL_GENERAL,
  "Old Age Home":                  T_RETAIL_GENERAL,
};

/*
 * Resolver — turns whatever string is saved on `business.businessType` into
 * a valid TRIAGE_DEFAULTS_BY_CATEGORY key.
 *
 * Why this is needed: depending on how the agent was created, the saved
 * value can be ANY of:
 *   - The parent `type` string (e.g. "HVAC Services") — exact match
 *   - A subtype string (e.g. "HVAC Installation", "Residential HVAC")
 *   - The Figma display label (e.g. "HVAC - Cooling & Heating")
 *   - A custom user-typed string when they picked "Add new"
 *
 * Strategy (most → least specific):
 *   1. Exact match against TRIAGE_DEFAULTS_BY_CATEGORY keys (fast path)
 *   2. Walk businessCategories.js — find the parent `type` whose own type
 *      string OR any of its subtypes (case-insensitive) equals the saved
 *      value. Return that parent type if it's keyed in our map.
 *   3. Substring match against parent type strings (e.g. saved value
 *      contains "HVAC" → match "HVAC Services"). Last-resort heuristic
 *      for free-text custom businesses that look related.
 *   4. Fallback: T_DEFAULT generic 4 rows.
 */
const resolveCategoryKey = (businessType) => {
  if (!businessType) return null;
  const trimmed = String(businessType).trim();
  if (!trimmed) return null;

  // 1. Exact match — fastest path.
  if (TRIAGE_DEFAULTS_BY_CATEGORY[trimmed]) return trimmed;

  // 2 + 3 require businessCategories.js — lazy-require to avoid eager
  // bundle bloat at module load.
  let businessTypes;
  try {
    businessTypes = require("./businessCategories").businessTypes || [];
  } catch {
    businessTypes = [];
  }

  const lowered = trimmed.toLowerCase();

  // 2. Subtype lookup — find the parent whose `type` OR any subtype
  //    matches our value (case-insensitive).
  for (const bt of businessTypes) {
    const parentType = bt?.type;
    if (!parentType) continue;

    if (parentType.toLowerCase() === lowered) {
      return TRIAGE_DEFAULTS_BY_CATEGORY[parentType] ? parentType : null;
    }

    const subtypes = Array.isArray(bt?.subtypes) ? bt.subtypes : [];
    if (subtypes.some((s) => String(s).toLowerCase() === lowered)) {
      return TRIAGE_DEFAULTS_BY_CATEGORY[parentType] ? parentType : null;
    }
  }

  // 3. Substring + leading-token heuristic. Order: longest parent name
  //    first so multi-word keys (e.g. "Real Estate Broker") win over
  //    short ones in case of ambiguity.
  const sortedKeys = Object.keys(TRIAGE_DEFAULTS_BY_CATEGORY).sort(
    (a, b) => b.length - a.length,
  );

  // Pass A — bidirectional substring covers Figma labels and free-text
  // variants where one fully contains the other (e.g. "HVAC - Cooling &
  // Heating" wouldn't match HVAC Services with this alone, but
  // "HVAC Services - Owner" does).
  for (const key of sortedKeys) {
    const keyLower = key.toLowerCase();
    if (lowered.includes(keyLower) || keyLower.includes(lowered)) {
      return key;
    }
  }

  // Pass B — leading-token match. Catches subtypes that share the
  // leading vertical word with their parent but diverge after it
  // (e.g. "HVAC Installation" / "HVAC Repair & Maintenance" both share
  // "hvac" with parent "HVAC Services" but neither side substring-
  // contains the other). Token must be length > 3 to avoid spurious
  // matches on short stop-word-ish tokens.
  for (const key of sortedKeys) {
    const firstToken = key.toLowerCase().split(/\s+/)[0];
    if (!firstToken || firstToken.length <= 3) continue;
    // Match either: value's leading word, or any word in the value
    // (handles "Residential HVAC Services" → matches "HVAC Services").
    const valueTokens = lowered.split(/\s+/);
    if (valueTokens.includes(firstToken)) return key;
  }

  return null;
};

/**
 * Returns the defaults for a category (or the spec's generic fallback).
 * Always returns a fresh array of fresh row objects so callers can safely
 * mutate without polluting the templates.
 *
 * @param {string} businessType — exact type string, subtype, label, or
 *   custom name. Resolver normalizes via businessCategories.js (see
 *   `resolveCategoryKey` above).
 * @returns {Array<{condition: string, notes: string}>}
 */
export const getTriageDefaultsForCategory = (businessType) => {
  const matchedKey = resolveCategoryKey(businessType);
  const template = matchedKey
    ? TRIAGE_DEFAULTS_BY_CATEGORY[matchedKey]
    : T_DEFAULT;

  // One-line debug so the live console shows what we matched. Easy to
  // grep if a vertical's defaults aren't showing as expected.
  console.log(
    "[triage-defaults] businessType=",
    JSON.stringify(businessType),
    "→ matchedKey=",
    matchedKey || "(none → _default fallback)",
    "→ rows=",
    template.length,
  );

  return template.map((r) => ({ ...r }));
};

export default TRIAGE_DEFAULTS_BY_CATEGORY;
